import React, { useState, useEffect, useMemo } from 'react';

import Input from '@/components/common/Input';
import Text from '@/components/common/Text';

interface ScoreboardProps {
  isEdit?: boolean;
  propsScoreboardValues?: ScoreboardValues;
  onInputTeam1?: (value: string) => void;
  onInputTeam2?: (value: string) => void;
  onChangeTeam1FirstHalfScore?: (value: number | undefined) => void;
  onChangeTeam2FirstHalfScore?: (value: number | undefined) => void;
  onChangeTeam1SecondHalfScore?: (value: number | undefined) => void;
  onChangeTeam2SecondHalfScore?: (value: number | undefined) => void;
  onChangeTeam1ExFirstScore?: (value: number | undefined) => void;
  onChangeTeam2ExFirstScore?: (value: number | undefined) => void;
  onChangeTeam1ExSecondScore?: (value: number | undefined) => void;
  onChangeTeam2ExSecondScore?: (value: number | undefined) => void;
  onChangeTeam1PKScore?: (value: number | undefined) => void;
  onChangeTeam2PKScore?: (value: number | undefined) => void;
}

interface ScoreboardValues {
  valTeam1: string | undefined;
  valTeam2: string | undefined;
  valTeam1FirstHalfScore: number | undefined;
  valTeam2FirstHalfScore: number | undefined;
  valTeam1SecondHalfScore: number | undefined;
  valTeam2SecondHalfScore: number | undefined;
  valTeam1ExtraFirstHalfScore: number | undefined;
  valTeam2ExtraFirstHalfScore: number | undefined;
  valTeam1ExtraSecondHalfScore: number | undefined;
  valTeam2ExtraSecondHalfScore: number | undefined;
  valTeam1PKScore: number | undefined;
  valTeam2PKScore: number | undefined;
}

/**
 * スコアボードコンポーネント
 * @param param0
 * @returns
 */
const Scoreboard: React.FC<ScoreboardProps> = ({
  isEdit = true,
  propsScoreboardValues,
  onInputTeam1,
  onInputTeam2,
  onChangeTeam1FirstHalfScore,
  onChangeTeam2FirstHalfScore,
  onChangeTeam1SecondHalfScore,
  onChangeTeam2SecondHalfScore,
  onChangeTeam1ExFirstScore,
  onChangeTeam2ExFirstScore,
  onChangeTeam1ExSecondScore,
  onChangeTeam2ExSecondScore,
  onChangeTeam1PKScore,
  onChangeTeam2PKScore
}) => {
  const [team1, setTeam1] = useState<string | undefined>(undefined);
  const [team2, setTeam2] = useState<string | undefined>(undefined);
  const [firstHalfScoreTeam1, setFirstHalfScoreTeam1] = useState<number | undefined>(undefined);
  const [firstHalfScoreTeam2, setFirstHalfScoreTeam2] = useState<number | undefined>(undefined);
  const [secondHalfScoreTeam1, setSecondHalfScoreTeam1] = useState<number | undefined>(undefined);
  const [secondHalfScoreTeam2, setSecondHalfScoreTeam2] = useState<number | undefined>(undefined);
  const [extraFirstHalfScoreTeam1, setExtraFirstHalfScoreTeam1] = useState<number | undefined>(undefined);
  const [extraFirstHalfScoreTeam2, setExtraFirstHalfScoreTeam2] = useState<number | undefined>(undefined);
  const [extraSecondHalfScoreTeam1, setExtraSecondHalfScoreTeam1] = useState<number | undefined>(undefined);
  const [extraSecondHalfScoreTeam2, setExtraSecondHalfScoreTeam2] = useState<number | undefined>(undefined);
  const [pkScoreTeam1, setPKScoreTeam1] = useState<number | undefined>(undefined);
  const [pkScoreTeam2, setPKScoreTeam2] = useState<number | undefined>(undefined);

  const getConvertedScore = (score: number | undefined): number => {
    return score ?? 0;
  };

  // トータルスコアチーム1
  const totalScoreTeam1 = useMemo(() => {
    return getConvertedScore(firstHalfScoreTeam1) + getConvertedScore(secondHalfScoreTeam1);
  }, [firstHalfScoreTeam1, secondHalfScoreTeam1]);

  // トータルスコアチーム2
  const totalScoreTeam2 = useMemo(() => {
    return getConvertedScore(firstHalfScoreTeam2) + getConvertedScore(secondHalfScoreTeam2);
  }, [firstHalfScoreTeam2, secondHalfScoreTeam2]);

  const handleInputTeam1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tmpVal = e.target.value;
    // 入力値が空白のみの場合、未入力と見なす
    const normalizedVal = tmpVal.trim().length === 0 ? '' : tmpVal;
    setTeam1(normalizedVal);
    onInputTeam1 && onInputTeam1(normalizedVal);
  };

  const handleInputTeam2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tmpVal = e.target.value;
    // 入力値が空白のみの場合、未入力と見なす
    const normalizedVal = tmpVal.trim().length === 0 ? '' : tmpVal;
    setTeam2(normalizedVal);
    onInputTeam2 && onInputTeam2(normalizedVal);
  };

  // スコアインクリメント
  const increment = (
    val: number | undefined,
    callbackHandler?: (val: number | undefined) => void
  ): number | undefined => {
    if (val === undefined) {
      callbackHandler && callbackHandler(0);
      return 0;
    } else if (val === 99) {
      callbackHandler && callbackHandler(99);
      return 99;
    } else {
      const tempVal = val + 1;
      callbackHandler && callbackHandler(tempVal);
      return tempVal;
    }
  };

  // スコアインデクリメント
  const decrement = (
    val: number | undefined,
    callbackHandler?: (val: number | undefined) => void
  ): number | undefined => {
    if (val === undefined || val === 0) {
      callbackHandler && callbackHandler(undefined);
      return undefined;
    } else {
      const tempVal = val - 1;
      callbackHandler && callbackHandler(tempVal);
      return tempVal;
    }
  };

  // 画面表示用スコア
  const getDisplayedScore = (score: number | undefined): string => {
    return score === undefined ? '-' : score.toString();
  };

  useEffect(() => {
    if (propsScoreboardValues) {
      setTeam1(!propsScoreboardValues.valTeam1 ? '' : propsScoreboardValues.valTeam1);
      setTeam2(!propsScoreboardValues.valTeam2 ? '' : propsScoreboardValues.valTeam2);
      setFirstHalfScoreTeam1(propsScoreboardValues.valTeam1FirstHalfScore);
      setFirstHalfScoreTeam2(propsScoreboardValues.valTeam2FirstHalfScore);
      setSecondHalfScoreTeam1(propsScoreboardValues.valTeam1SecondHalfScore);
      setSecondHalfScoreTeam2(propsScoreboardValues.valTeam2SecondHalfScore);
      setExtraFirstHalfScoreTeam1(propsScoreboardValues.valTeam1ExtraFirstHalfScore);
      setExtraFirstHalfScoreTeam2(propsScoreboardValues.valTeam2ExtraFirstHalfScore);
      setExtraSecondHalfScoreTeam1(propsScoreboardValues.valTeam1ExtraSecondHalfScore);
      setExtraSecondHalfScoreTeam2(propsScoreboardValues.valTeam2ExtraSecondHalfScore);
      setPKScoreTeam1(propsScoreboardValues.valTeam1PKScore);
      setPKScoreTeam2(propsScoreboardValues.valTeam2PKScore);
    }
  }, [propsScoreboardValues]);

  // 左部：スコア入力
  const renderScoreLeftCell = (
    score: number | undefined,
    incrementHandler: () => void,
    decrementHandler: () => void,
    isEdit: boolean
  ) => {
    return (
      <>
        <td className="text-xl text-center">
          <div className="flex justify-center items-center">
            {isEdit && (
              <div className="w-[96px] flex justify-center items-center">
                <button
                  type="button"
                  className="flex justify-center items-center m-1 bg-transparent border border-green-500 hover:bg-green-500 text-green-500 hover:text-white rounded-full w-8 h-8"
                  onClick={decrementHandler}>
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 12h14"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="flex justify-center items-center m-1 bg-transparent border border-green-500 hover:bg-green-500 text-green-500 hover:text-white rounded-full w-8 h-8"
                  onClick={incrementHandler}>
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 12h14m-7 7V5"
                    />
                  </svg>
                </button>
              </div>
            )}
            <div className="grow text-xl font-footballlog py-1">{getDisplayedScore(score)}</div>
          </div>
        </td>
      </>
    );
  };

  // 右部：スコア入力
  const renderScoreRightCell = (
    score: number | undefined,
    incrementHandler: () => void,
    decrementHandler: () => void,
    isEdit: boolean
  ) => {
    return (
      <>
        <td className="text-xl text-center">
          <div className="flex justify-center items-center">
            <div className="grow text-xl font-footballlog py-1">{getDisplayedScore(score)}</div>
            {isEdit && (
              <div className="w-[96px] flex justify-center items-center">
                <button
                  type="button"
                  className="flex justify-center items-center m-1 bg-transparent border border-green-500 hover:bg-green-500 text-green-500 hover:text-white rounded-full w-8 h-8"
                  onClick={incrementHandler}>
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 12h14m-7 7V5"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="flex justify-center items-center m-1 bg-transparent border border-green-500 hover:bg-green-500 text-green-500 hover:text-white rounded-full w-8 h-8"
                  onClick={decrementHandler}>
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 12h14"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </td>
      </>
    );
  };

  return (
    <div
      className={`p-4 text-gray-900 rounded-lg ${isEdit ? 'bg-gray-200' : 'bg-gray-200'} bg-no-repeat bg-center`}
      style={{ backgroundImage: "url('/images/soccerball_gray.svg')", backgroundSize: '105%', width: '100%' }}>
      <table className="table-fixed border-separate border-spacing-0 border-none w-full border-slate-500">
        <tbody>
          <tr>
            <td className="text-center">
              {isEdit ? (
                <Input
                  id="team1"
                  type="text"
                  className="w-full text-center"
                  placeholder="Team"
                  maxLength={50}
                  value={team1 || ''}
                  onChange={handleInputTeam1}
                />
              ) : (
                <Text className="text-lg">{team1}</Text>
              )}
            </td>
            {isEdit ? (
              <td className="w-8 text-center">
                <span className="txt lg gray font-semibold">:</span>
              </td>
            ) : (
              <td className="w-20 text-center">
                <span className="txt lg gray font-semibold">:</span>
              </td>
            )}
            <td className="text-center">
              {isEdit ? (
                <Input
                  id="team2"
                  type="text"
                  className="w-full text-center"
                  placeholder="Team"
                  maxLength={50}
                  value={team2 || ''}
                  onChange={handleInputTeam2}
                />
              ) : (
                <Text className="text-lg">{team2}</Text>
              )}
            </td>
          </tr>
        </tbody>
      </table>
      <table className="table-fixed border-separate border-spacing-0 border-none w-full border-slate-500">
        <tbody>
          <tr>
            <td>
              <div className="flex justify-center items-center py-2">
                {isEdit && <div className="w-[96px]"></div>}
                <div className="grow text-center text-3xl font-footballlog font-semibold">{totalScoreTeam1}</div>
              </div>
            </td>
            <td className="w-20 text-center font-footballlog font-semibold">
              <span>TOTAL</span>
            </td>
            <td>
              <div className="flex justify-center items-center py-2">
                <div className="grow text-center text-3xl font-footballlog font-semibold">{totalScoreTeam2}</div>
                {isEdit && <div className="w-[96px]"></div>}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <hr className="border-none rounded-full h-[6px] bg-gray-300" />
      <table className="table-fixed border-separate border-spacing-0 border-none w-full border-slate-500">
        <tbody>
          {/* 前半 */}
          <tr>
            {renderScoreLeftCell(
              firstHalfScoreTeam1,
              () => setFirstHalfScoreTeam1(increment(firstHalfScoreTeam1, onChangeTeam1FirstHalfScore)),
              () => setFirstHalfScoreTeam1(decrement(firstHalfScoreTeam1, onChangeTeam1FirstHalfScore)),
              isEdit
            )}
            <td className="w-20 text-center font-footballlog">
              <span>1st</span>
            </td>
            {renderScoreRightCell(
              firstHalfScoreTeam2,
              () => setFirstHalfScoreTeam2(increment(firstHalfScoreTeam2, onChangeTeam2FirstHalfScore)),
              () => setFirstHalfScoreTeam2(decrement(firstHalfScoreTeam2, onChangeTeam2FirstHalfScore)),
              isEdit
            )}
          </tr>
          {/* 後半 */}
          <tr>
            {renderScoreLeftCell(
              secondHalfScoreTeam1,
              () => setSecondHalfScoreTeam1(increment(secondHalfScoreTeam1, onChangeTeam1SecondHalfScore)),
              () => setSecondHalfScoreTeam1(decrement(secondHalfScoreTeam1, onChangeTeam1SecondHalfScore)),
              isEdit
            )}
            <td className="w-20 text-center font-footballlog">
              <span>2nd</span>
            </td>
            {renderScoreRightCell(
              secondHalfScoreTeam2,
              () => setSecondHalfScoreTeam2(increment(secondHalfScoreTeam2, onChangeTeam2SecondHalfScore)),
              () => setSecondHalfScoreTeam2(decrement(secondHalfScoreTeam2, onChangeTeam2SecondHalfScore)),
              isEdit
            )}
          </tr>
          {/* 延長前半 */}
          <tr>
            {renderScoreLeftCell(
              extraFirstHalfScoreTeam1,
              () => setExtraFirstHalfScoreTeam1(increment(extraFirstHalfScoreTeam1, onChangeTeam1ExFirstScore)),
              () => setExtraFirstHalfScoreTeam1(decrement(extraFirstHalfScoreTeam1, onChangeTeam1ExFirstScore)),
              isEdit
            )}
            <td className="w-20 text-center font-footballlog">
              <span>Ex1</span>
            </td>
            {renderScoreRightCell(
              extraFirstHalfScoreTeam2,
              () => setExtraFirstHalfScoreTeam2(increment(extraFirstHalfScoreTeam2, onChangeTeam2ExFirstScore)),
              () => setExtraFirstHalfScoreTeam2(decrement(extraFirstHalfScoreTeam2, onChangeTeam2ExFirstScore)),
              isEdit
            )}
          </tr>
          {/* 延長後半 */}
          <tr>
            {renderScoreLeftCell(
              extraSecondHalfScoreTeam1,
              () => setExtraSecondHalfScoreTeam1(increment(extraSecondHalfScoreTeam1, onChangeTeam1ExSecondScore)),
              () => setExtraSecondHalfScoreTeam1(decrement(extraSecondHalfScoreTeam1, onChangeTeam1ExSecondScore)),
              isEdit
            )}
            <td className="w-20 text-center font-footballlog">
              <span>Ex2</span>
            </td>
            {renderScoreRightCell(
              extraSecondHalfScoreTeam2,
              () => setExtraSecondHalfScoreTeam2(increment(extraSecondHalfScoreTeam2, onChangeTeam2ExSecondScore)),
              () => setExtraSecondHalfScoreTeam2(decrement(extraSecondHalfScoreTeam2, onChangeTeam2ExSecondScore)),
              isEdit
            )}
          </tr>
          {/* PK */}
          <tr>
            {renderScoreLeftCell(
              pkScoreTeam1,
              () => setPKScoreTeam1(increment(pkScoreTeam1, onChangeTeam1PKScore)),
              () => setPKScoreTeam1(decrement(pkScoreTeam1, onChangeTeam1PKScore)),
              isEdit
            )}
            <td className="w-20 text-center font-footballlog">
              <span>PK</span>
            </td>
            {renderScoreRightCell(
              pkScoreTeam2,
              () => setPKScoreTeam2(increment(pkScoreTeam2, onChangeTeam2PKScore)),
              () => setPKScoreTeam2(decrement(pkScoreTeam2, onChangeTeam2PKScore)),
              isEdit
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Scoreboard;
